/**
 * PresentationFileView React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


import React from 'react';
import {withStyles} from "@material-ui/core";
import PropTypes from 'prop-types';
import PresentationSlideView from "./PresentationSlideView";
import PresentationTitle from "./PresentationTitle";
import PresentationProgressBar from "./PresentationProgressBar";
import MajusculeText from "./MajusculeText";
import MediaPanel from "../../base/MediaPanel";
import ParagraphText from "../../base/ParagraphText";
import {getMediaUrl} from '../../../operations/utils';


function getMediaTitle(mediaItem) {
    return mediaItem && mediaItem.fields ? mediaItem.fields.title : null;
}


/**
 * presentation file view
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
class PresentationFileView extends React.Component {

    render() {

        const presentationFile = this.props.presentationFile || {};
        const presentationFileFields = presentationFile.fields || {};
        const navigationIndex = this.props.navigationIndex || [];
        const chapters = presentationFileFields.chapters || [];
        const chapterIndex = navigationIndex[0] || 0;
        const fileIndex = navigationIndex[1] || 0;
        const currentChapter = chapterIndex > 0 && chapters[chapterIndex - 1];

        const slideData = currentChapter ? this.createChapterData(
            presentationFileFields,
            currentChapter,
            presentationFile.id + '/' + chapterIndex,
            fileIndex
        ) : this.createCoverData(
            presentationFileFields
        );

        return <PresentationSlideView
            screenIndex={this.props.screenIndex}
            data={slideData}
            index={chapterIndex}
            progressBar={<div className={this.props.classes.progressBar}>
                <PresentationProgressBar
                    count={chapters.length + 1}
                    index={chapterIndex}
                    label={chapterIndex > 0 ? "Kapitel " + chapterIndex : "Übersicht"}
                />
            </div>}
        />;
    }

    createCoverData(presentationFileFields) {

        const {title, subheading, image, teaser} = presentationFileFields;
        const presentationFile = this.props.presentationFile || {};
        const imageItem = this.getSingleReferencedObject(image);

        return {
            titleElement: null,
            teaserTag: teaser,
            teaserElement: <PresentationTitle
                className={this.props.classes.bottomAlignedTeaser}
                title={title}
                subheading={subheading}
            />,
            teaserImageUrl: null,
            chapterTitle: "",
            chapterTag: "teaser:" + teaser,
            chapterElement: <div className={this.props.classes.coverText}>{teaser}</div>,
            chapterImageUrl: getMediaUrl(imageItem) || presentationFile.imageurl,
        }
    }

    createChapterData(presentationFileFields, chapter, chapterId, fileIndex) {

        if (!chapter) return this.createErrorChapterData("Dieses Kapitel gibt es gar nicht");

        switch (chapter.type) {

            case "presentationtextchapter":
                return this.createTextChapterData(presentationFileFields, chapter);

            case "presentationimagechapter":
                return this.createImageChapterData(presentationFileFields, chapter, fileIndex);

            case "presentationvideochapter":
                return this.createVideoChapterData(presentationFileFields, chapter, chapterId, fileIndex);

            case "presentationinterviewchapter":
                return this.createInterviewChapterData(presentationFileFields, chapter, chapterId, fileIndex);

            default:
                return this.createErrorChapterData(presentationFileFields, chapter.type + " gibt es gar nicht");
        }
    }

    createTextChapterData(presentationFileFields, chapter) {

        const chapterFields = chapter.fields || {};
        const {title, text, image, audio} = chapterFields;
        const imageItem = this.getSingleReferencedObject(image);
        const audioItem = this.getSingleReferencedObject(audio);
        const titleElement = <PresentationTitle
            className={this.props.classes.topAlignedTitle}
            title={presentationFileFields.title}
            subheading={presentationFileFields.subheading}
        />;
        const teaserElement = <div className={this.props.classes.bottomAlignedTeaser}>
            <MajusculeText text={chapterFields.teaser} />
        </div>;
        const chapterElement = <div className="presentation-chapter-text">
            <ParagraphText text={text} />
        </div>;

        return {
            titleElement: titleElement,
            teaserTag: chapterFields.teaser,
            teaserElement: teaserElement,
            teaserImageUrl: getMediaUrl(imageItem),
            chapterTitle: title,
            chapterTag: "text:" + text,
            chapterElement: chapterElement,
            chapterImageUrl: null,
            backgroundAudioUrl: getMediaUrl(audioItem)
        };
    }

    createImageChapterData(presentationFileFields, chapter, fileIndex) {

        const chapterFields = chapter.fields || {};
        const {title, image} = chapterFields;
        const imageItem = this.getSingleReferencedObject(image, fileIndex);
        const titleElement = <PresentationTitle
            className={this.props.classes.topAlignedTitle}
            title={presentationFileFields.title}
            subheading={presentationFileFields.subheading}
        />;
        const teaserElement = <div className={this.props.classes.bottomAlignedTeaser}>
            <MajusculeText text={chapterFields.teaser} />
        </div>;

        return {
            titleElement: titleElement,
            teaserTag: chapterFields.teaser,
            teaserElement: teaserElement,
            teaserImageUrl: null,
            chapterTitle: getMediaTitle(imageItem) || title,
            chapterTag: "",
            chapterElement: null,
            chapterImageUrl: getMediaUrl(imageItem),
        }
    }

    createVideoChapterData(presentationFileFields, chapter, chapterId, fileIndex) {

        const chapterFields = chapter.fields || {};
        const {title, image, video} = chapterFields;
        const imageItem = this.getSingleReferencedObject(image);
        const videoItem = this.getSingleReferencedObject(video, fileIndex);
        const videoUrl = getMediaUrl(videoItem);
        const titleElement = <PresentationTitle
            className={this.props.classes.topAlignedTitle}
            title={presentationFileFields.title}
            subheading={presentationFileFields.subheading}
        />;
        const teaserElement = <div className={this.props.classes.bottomAlignedTeaser}>
            <MajusculeText text={chapterFields.teaser} />
        </div>;
        const chapterElement = <div className={this.props.classes.chapterVideo}>
            <MediaPanel
                volume={this.props.screenIndex == 1 ? 0 : undefined}
                url={videoUrl}
                full={true}
                showControls={false}
                playOnClick={false}
                autoplay={true}
                paused={this.props.paused}
                position={this.props.playbackPosition}
                otherFilesAsDownload={false}
                onPlaybackEnded={this.props.onPlaybackEnded}
            />
        </div>;

        // console.log("rendering MediaPanel paused = ", this.props.paused);

        return {
            titleElement: titleElement,
            teaserTag: chapterFields.teaser,
            teaserElement: teaserElement,
            teaserImageUrl: getMediaUrl(imageItem),
            chapterTitle: getMediaTitle(videoItem) || title,
            chapterTag: chapterId,
            chapterElement: chapterElement,
            chapterImageUrl: null,
        }
    }

    createInterviewChapterData(presentationFileFields, chapter, chapterId, fileIndex) {

        const chapterFields = chapter.fields || {};
        const {title, image, video} = chapterFields;
        const imageItem = this.getSingleReferencedObject(image);
        const videoItem = this.getSingleReferencedObject(video, fileIndex);
        const videoUrl = getMediaUrl(videoItem);
        const titleElement = <PresentationTitle
            className={this.props.classes.topAlignedTitle}
            title={presentationFileFields.title}
            subheading={presentationFileFields.subheading}
        />;
        const teaserElement = <div className={this.props.classes.bottomAlignedTeaser}>
            <MajusculeText text={chapterFields.teaser} />
        </div>;
        const chapterElement = <div className={this.props.classes.chapterVideo}>
            <MediaPanel
                volume={this.props.screenIndex == 1 ? 0 : undefined}
                url={videoUrl}
                full={true}
                showControls={false}
                playOnClick={false}
                autoplay={true}
                paused={this.props.paused}
                position={this.props.playbackPosition}
                otherFilesAsDownload={false}
                onPlaybackEnded={this.props.onPlaybackEnded}
            />
        </div>;

        return {
            titleElement: titleElement,
            teaserTag: chapterFields.teaser,
            teaserElement: teaserElement,
            teaserImageUrl: getMediaUrl(imageItem),
            chapterTitle: getMediaTitle(videoItem) || title,
            chapterTag: chapterId,
            chapterElement: chapterElement,
            chapterImageUrl: null,
        }
    }

    createErrorChapterData(presentationFileFields, message) {

        const titleElement = <PresentationTitle
            className={this.props.classes.topAlignedTitle}
            title={presentationFileFields.title}
            subheading={presentationFileFields.subheading}
        />;
        const text = "Wenn dieser Text zu sehen ist, hat jemand von uns was verbockt. Das tut uns wirklich leid.";
        const teaserElement = <div className={this.props.classes.bottomAlignedTeaser}>
            <MajusculeText text={message} />
        </div>;
        const chapterElement = <div className={this.props.classes.chapterText}>{text}</div>;

        return {
            titleElement: titleElement,
            teaserTag: message,
            teaserElement: teaserElement,
            teaserImageUrl: "/images/elephant_legs_illusion_pink.png",
            chapterTitle: "Hier hat sich ein Fehler eingeschlichen",
            chapterTag: "text:" + text,
            chapterElement: chapterElement,
            chapterImageUrl: null
        };
    }

    getSingleReferencedObject(field, index = 0) {
        if (!field || index < 0) return null;
        const items = this.props.findReferencedObjects(field);
        return items && items.length > index ? items[index] : null;
    }
}

PresentationFileView.propTypes = {
    screenIndex: PropTypes.number,
    presentationFile: PropTypes.object,
    navigationIndex: PropTypes.arrayOf(PropTypes.number),
    findReferencedObjects: PropTypes.func.isRequired,
    paused: PropTypes.bool,
    playbackPosition: PropTypes.number,
    onPlaybackEnded: PropTypes.func
};

const styles = theme => ({
    topAlignedTitle: {
        marginLeft: "8vw"
    },
    bottomAlignedTeaser: {
        position: "absolute",
        bottom: "0vw",
        left: "8vw",
        width: "84vw"
    },
    progressBar: {
        marginLeft: "8vw",
        marginRight: "8vw",
    },
    chapterVideo: {
        paddingLeft: "0vw",
        paddingTop: "5vw",
        width: "100vw"
    },
    coverText: {
        marginLeft: "8vw",
        marginRight: "8vw",
        fontSize: "6vw",
        lineHeight: 1.25
    }
});

export default withStyles(styles)(PresentationFileView);
